import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import GlobalFooter from '../components/GlobalFooter';
import {
  ArrowLeft, Crown, Check, Star, Zap, Shield,
  BarChart3, Target, Brain, Clock, Bell,
  CreditCard, Loader2, X, Sparkles, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRO_FEATURES = [
  { icon: BarChart3, title: 'Streak Tracking & Milestones', desc: 'Track consecutive check-in days with milestone celebrations at 7, 14, 30, 60, and 90 days' },
  { icon: Brain, title: 'Wellness Journal & Trends', desc: 'Daily wellness logging with trend charts showing 7/30/90-day rolling averages for energy, sleep, mood, digestion, and focus' },
  { icon: Sparkles, title: 'Correlation Insights', desc: 'AI-powered analysis: "Your energy is 35% higher on days you take supplements"' },
  { icon: Target, title: 'My Stack Tracker', desc: 'Manage your active supplements with restock reminders and a visual daily schedule' },
  { icon: Bell, title: 'Push Reminders', desc: 'Configurable daily reminder: "Don\'t break your streak!"' },
  { icon: Clock, title: 'Unlimited Revisions', desc: 'Revise your health assessment anytime to keep your supplement plan current' },
];

export default function SubscriptionPage() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('yearly');
  const [cancelling, setCancelling] = useState(false);

  const sessionId = searchParams.get('session_id');

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/subscription/status`, { headers: getAuthHeader() });
      setStatus(res.data);
    } catch (err) {
      console.error('Subscription status error:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (!user) { navigate('/auth?redirect=/subscription'); return; }
    fetchStatus();
  }, [user, navigate, fetchStatus]);

  // Handle return from Stripe checkout
  useEffect(() => {
    if (!sessionId || !user) return;
    const checkPayment = async () => {
      try {
        const res = await axios.get(`${API}/subscription/checkout/status/${sessionId}`, { headers: getAuthHeader() });
        if (res.data.payment_status === 'paid') {
          toast.success('Welcome to Vitala Pro!');
          fetchStatus();
        }
      } catch (err) {
        console.error('Payment check error:', err);
      }
    };
    checkPayment();
  }, [sessionId, user, getAuthHeader, fetchStatus]);

  const handleCheckout = async (plan) => {
    setCheckingOut(true);
    try {
      const res = await axios.post(`${API}/subscription/checkout`, {
        plan,
        origin_url: window.location.origin
      }, { headers: getAuthHeader() });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error('Unable to start checkout. Please try again.');
      setCheckingOut(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API}/subscription/cancel`, { reason: '' }, { headers: getAuthHeader() });
      toast.success('Subscription cancelled');
      fetchStatus();
    } catch (err) {
      toast.error('Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const isPro = status?.tier === 'pro';
  const paywallActive = status?.paywall_active;

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col">
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-primary" data-testid="subscription-back">
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </Link>
          <Logo size="small" linkTo="/" />
          <div className="w-16" />
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-8 w-full" data-testid="subscription-page">
        {isPro ? (
          /* Active Pro Subscription */
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                <Crown className="w-8 h-8 text-amber-600" />
              </div>
              <h1 className="text-2xl font-bold text-[#1A1A1A] mb-2">
                {!paywallActive ? 'All Pro Features Unlocked' : 'Vitala Pro Active'}
              </h1>
              <p className="text-muted-foreground">
                {!paywallActive
                  ? 'You currently have free access to all Pro features during our early access period.'
                  : 'You have full access to all Pro features'}
              </p>
            </div>

            {/* Feature list */}
            <div className="bg-white rounded-2xl border border-border p-6 mb-6">
              <h3 className="font-semibold text-[#1A1A1A] mb-4">
                {!paywallActive ? 'Your Included Features' : 'Subscription Details'}
              </h3>
              {paywallActive && status.subscription?.plan_name !== 'Free Access' ? (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Plan</span>
                    <span className="font-medium">{status.subscription?.plan_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Started</span>
                    <span className="font-medium">{new Date(status.subscription?.started_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Renews</span>
                    <span className="font-medium">{new Date(status.subscription?.expires_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {PRO_FEATURES.map((f, i) => {
                    const Icon = f.icon;
                    return (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-medium text-[#1A1A1A]">{f.title}</h4>
                            <Check className="w-3.5 h-3.5 text-emerald-500" />
                          </div>
                          <p className="text-xs text-muted-foreground">{f.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {paywallActive && status.subscription?.plan_name !== 'Free Access' && (
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={cancelling}
                className="w-full text-red-600 border-red-200 hover:bg-red-50"
                data-testid="cancel-subscription-btn"
              >
                {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
              </Button>
            )}
          </motion.div>
        ) : (
          /* Pro Upgrade Page */
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center mx-auto mb-4">
                <Crown className="w-8 h-8 text-amber-600" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-2">Upgrade to Vitala Pro</h1>
              <p className="text-muted-foreground max-w-md mx-auto">
                Unlock the full potential of your supplement journey with advanced tracking, insights, and personalized tools.
              </p>
            </div>

            {/* Plan Selector */}
            <div className="grid grid-cols-2 gap-3 mb-8 max-w-md mx-auto">
              <button
                onClick={() => setSelectedPlan('monthly')}
                className={`p-4 rounded-xl border-2 text-center transition-all ${
                  selectedPlan === 'monthly'
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid="plan-monthly"
              >
                <p className="text-2xl font-bold text-[#1A1A1A]">$4.99</p>
                <p className="text-xs text-muted-foreground">per month</p>
              </button>
              <button
                onClick={() => setSelectedPlan('yearly')}
                className={`p-4 rounded-xl border-2 text-center transition-all relative ${
                  selectedPlan === 'yearly'
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid="plan-yearly"
              >
                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-emerald-500 text-white text-[10px] font-bold rounded-full">
                  SAVE 17%
                </div>
                <p className="text-2xl font-bold text-[#1A1A1A]">$49.99</p>
                <p className="text-xs text-muted-foreground">per year ($4.17/mo)</p>
              </button>
            </div>

            {/* Features List */}
            <div className="bg-white rounded-2xl border border-border p-6 mb-6">
              <h3 className="font-semibold text-[#1A1A1A] mb-4">Everything in Pro</h3>
              <div className="space-y-4">
                {PRO_FEATURES.map((f, i) => {
                  const Icon = f.icon;
                  return (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-[#1A1A1A]">{f.title}</h4>
                        <p className="text-xs text-muted-foreground">{f.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Checkout Button */}
            <Button
              onClick={() => handleCheckout(selectedPlan)}
              disabled={checkingOut}
              className="w-full btn-primary py-3 text-base"
              data-testid="checkout-btn"
            >
              {checkingOut ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
              ) : (
                <><CreditCard className="w-4 h-4 mr-2" /> Subscribe — ${selectedPlan === 'yearly' ? '49.99/year' : '4.99/month'}</>
              )}
            </Button>

            <p className="text-[10px] text-muted-foreground text-center mt-3">
              Cancel anytime. Secure payment via Stripe.
            </p>
          </motion.div>
        )}
      </main>

      <GlobalFooter />
    </div>
  );
}
